(function () {
  'use strict';

  var MOBILE = '(max-width: 749px)';
  var FOCUSABLE = 'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  class EbMobileNav extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.eb-mnav__bar');
      this.drawer = this.querySelector('[id="eb-MnavDrawer"]');
      this.openBtn = this.querySelector('[data-eb-mnav-open]');
      this.closeBtn = this.querySelector('[data-eb-mnav-close]');
      this.menuItem = this.openBtn ? this.openBtn.closest('.eb-mnav__item--menu') : null;
      this.isOpen = false;

      // Move the drawer (dim + panel) to <body> so no ancestor's transform/
      // overflow/stacking context can trap or clip the full-viewport overlay.
      if (this.drawer && this.drawer.parentNode !== document.body) {
        document.body.appendChild(this.drawer);
      }

      this.onResize = this.reserveSpace.bind(this);
      window.addEventListener('resize', this.onResize);
      window.addEventListener('orientationchange', this.onResize);
      this.reserveSpace();

      if (this.openBtn && this.drawer) {
        this.openBtn.addEventListener('click', this.open.bind(this));
        if (this.closeBtn) this.closeBtn.addEventListener('click', this.close.bind(this));
        // Click on the dim area (outside the panel) closes.
        this.drawer.addEventListener('click', function (event) {
          if (event.target === this.drawer) this.close();
        }.bind(this));

        this._onKeydown = this.handleKeydown.bind(this);
        document.addEventListener('keydown', this._onKeydown);
      }

      this.initAccordions();
    }

    /* Smooth open/close for the drawer's <details> accordions — native <details>
       just snaps, so we animate the content height and drive the caret off an
       `.is-open` class. Falls back to the native snap under reduced-motion. */
    initAccordions() {
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var self = this;
      var scope = this.drawer || this;
      var items = scope.querySelectorAll('.eb-mnav__acc-details');
      Array.prototype.forEach.call(items, function (d) {
        var summary = d.querySelector('summary');
        var content = d.querySelector('.eb-mnav__sub');
        if (!summary || !content) return;
        if (d.open) d.classList.add('is-open');
        summary.addEventListener('click', function (event) {
          event.preventDefault();
          if (d.dataset.animating) return;
          if (d.open) self.collapseAcc(d, content, reduce);
          else self.expandAcc(d, content, reduce);
        });
      });
    }

    expandAcc(d, content, reduce) {
      d.open = true;
      d.classList.add('is-open');
      if (reduce) return;
      d.dataset.animating = '1';
      content.style.overflow = 'hidden';
      content.style.height = '0px';
      var target = content.scrollHeight;
      // Collapse the bottom padding too: with box-sizing:border-box the box
      // can't shrink below its padding, so a bare height:0 would leave (and
      // pop from) an 8px stub. Animating padding alongside height gives a true
      // zero and a smooth open/close.
      content.style.paddingBottom = '0px';
      window.requestAnimationFrame(function () {
        content.style.transition = 'height 0.26s ease, padding-bottom 0.26s ease';
        content.style.height = target + 'px';
        content.style.paddingBottom = '';
      });
      this.onAccEnd(d, content, false);
    }

    collapseAcc(d, content, reduce) {
      d.classList.remove('is-open');
      if (reduce) {
        d.open = false;
        return;
      }
      d.dataset.animating = '1';
      content.style.overflow = 'hidden';
      content.style.height = content.scrollHeight + 'px';
      // Force reflow so the starting height is committed before we animate to 0.
      void content.offsetHeight;
      window.requestAnimationFrame(function () {
        content.style.transition = 'height 0.26s ease, padding-bottom 0.26s ease';
        content.style.height = '0px';
        content.style.paddingBottom = '0px';
      });
      this.onAccEnd(d, content, true);
    }

    onAccEnd(d, content, closing) {
      var end = function (event) {
        if (event && event.propertyName !== 'height') return;
        if (closing) d.open = false;
        content.style.transition = '';
        content.style.height = '';
        content.style.overflow = '';
        content.style.paddingBottom = '';
        delete d.dataset.animating;
        content.removeEventListener('transitionend', end);
      };
      content.addEventListener('transitionend', end);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
      if (this.isOpen) this.unlock();
      this.clearReserve();
      // Clean up the portaled drawer.
      if (this.drawer && this.drawer.parentNode === document.body) this.drawer.remove();
    }

    /* Reserve page-bottom space equal to the (safe-area-inclusive) bar height so
       fixed bar never covers page content. Cleared above the mobile breakpoint.
       The reserve goes on <html>, not on this element: our own drawer is
       portaled to <body> (so it can't inherit from here), and fixed overlays
       owned by other components — the cart drawer — need to read it too. */
    reserveSpace() {
      if (!this.bar) return;
      var root = document.documentElement;
      if (!window.matchMedia(MOBILE).matches) {
        this.clearReserve();
        return;
      }
      var h = Math.round(this.bar.getBoundingClientRect().height);
      if (!h) return;
      document.body.style.paddingBottom = h + 'px';
      root.style.setProperty('--eb-mnav-reserve', h + 'px');
      root.classList.add('eb-mnav-on');
    }

    clearReserve() {
      document.body.style.paddingBottom = '';
      document.documentElement.style.removeProperty('--eb-mnav-reserve');
      document.documentElement.classList.remove('eb-mnav-on');
    }

    handleKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event);
    }

    trapFocus(event) {
      var nodes = this.drawer.querySelectorAll(FOCUSABLE);
      var focusable = Array.prototype.filter.call(nodes, function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open() {
      if (this.isOpen || !this.drawer) return;
      this.isOpen = true;
      this.lastFocused = document.activeElement;

      this.drawer.hidden = false;
      // Next frame so the transition runs from the hidden state.
      window.requestAnimationFrame(function () {
        this.drawer.classList.add('is-open');
      }.bind(this));

      this.lock();
      if (this.openBtn) this.openBtn.setAttribute('aria-expanded', 'true');
      if (this.menuItem) this.menuItem.classList.add('is-open');

      var closer = this.closeBtn || this.drawer;
      window.requestAnimationFrame(function () {
        closer.focus();
      });
    }

    close() {
      if (!this.isOpen || !this.drawer) return;
      this.isOpen = false;

      this.drawer.classList.remove('is-open');
      this.unlock();
      if (this.openBtn) this.openBtn.setAttribute('aria-expanded', 'false');
      if (this.menuItem) this.menuItem.classList.remove('is-open');

      var drawer = this.drawer;
      var done = function () {
        if (!drawer.classList.contains('is-open')) {
          drawer.hidden = true;
        }
        drawer.removeEventListener('transitionend', done);
      };
      drawer.addEventListener('transitionend', done);
      // Fallback in case transitionend doesn't fire (reduced-motion, etc.).
      window.setTimeout(done, 400);

      if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
    }

    lock() {
      document.documentElement.classList.add('eb-mnav-open');
    }

    unlock() {
      document.documentElement.classList.remove('eb-mnav-open');
    }
  }

  if (!customElements.get('eb-mobile-nav')) {
    customElements.define('eb-mobile-nav', EbMobileNav);
  }
})();